import React, { useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Stack, Button, Divider, Chip,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import * as XLSX from 'xlsx';
import PopoverFiltroRelatorio from './PopoverFiltroRelatorio';
import generatePdfRelatorioCustosServico from './EditPdfRelatorioCustosServico';
import { cores } from '../styles/styleUtils';

// ── Paleta TriAxis — aliases dos tokens de styleUtils ─────────────────────
const LARANJA  = cores.laranja;
const CINZA    = cores.chumbo;
const CINZA2   = cores.cinzaMedio;
const LARANJA2 = cores.laranjaEscuro;
const VERDE    = '#2e7d32';
const VERMELHO = '#c62828';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNum = (v) => Number(v || 0).toLocaleString('pt-BR');

// ── KPI Card ───────────────────────────────────────────────────────────────
const KpiCard = ({ label, valor, destaque, cor }) => (
  <Box sx={{
    flex: 1, minWidth: 130, textAlign: 'center',
    border: `1px solid ${destaque ? LARANJA : '#DDD'}`,
    borderRadius: 1, p: 1.5,
    backgroundColor: destaque ? '#FFF3E0' : '#FAFAFA',
    borderLeft: `4px solid ${cor || (destaque ? LARANJA : CINZA)}`,
  }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="subtitle1" fontWeight="bold" color={cor || (destaque ? LARANJA : CINZA)}>{valor}</Typography>
  </Box>
);

// ── Título de seção ────────────────────────────────────────────────────────
const SecTitle = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 4 }}>
    <Box sx={{ width: 4, height: 20, backgroundColor: LARANJA, borderRadius: 1 }} />
    <Typography variant="subtitle1" fontWeight="bold" color={CINZA}>{children}</Typography>
  </Box>
);

// ── Tabela principal (Etapa / Qtd / Venda / Custo / Margem) ────────────────
const TabelaCustos = ({ dados }) => (
  <Box mt={3}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: '#FFF3E0' }}>
          {['Etapa', 'Quantidade', 'Venda (R$)', 'Custo (R$)', 'Margem (R$)'].map((h) => (
            <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: 11 }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {dados.map((e, i) => (
          <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF3E0' } }}>
            <TableCell>{e.etapa}</TableCell>
            <TableCell>{fmtNum(e.quantidade)}</TableCell>
            <TableCell>{fmtBRL(e.valorVenda)}</TableCell>
            <TableCell>{fmtBRL(e.custo)}</TableCell>
            <TableCell sx={{ color: e.margem >= 0 ? VERDE : VERMELHO, fontWeight: 'bold' }}>{fmtBRL(e.margem)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

// ── Tabela de segmentação (Tipo de Serviço / Responsável) ──────────────────
const TabelaSegmentacaoCusto = ({ dados }) => (
  <Box mt={3}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
          {['Grupo', 'Quantidade', 'Venda (R$)', 'Custo (R$)', 'Margem (R$)', 'Margem (%)'].map((h) => (
            <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: 11 }}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {dados.map((g, i) => (
          <TableRow key={i} sx={{ '&:hover': { backgroundColor: '#FFF3E0' } }}>
            <TableCell sx={{ width: 200 }}>{g.grupo}</TableCell>
            <TableCell>{fmtNum(g.quantidade)}</TableCell>
            <TableCell>{fmtBRL(g.valorVenda)}</TableCell>
            <TableCell>{fmtBRL(g.custo)}</TableCell>
            <TableCell sx={{ color: g.margem >= 0 ? VERDE : VERMELHO, fontWeight: 'bold' }}>{fmtBRL(g.margem)}</TableCell>
            <TableCell align="center">{Number(g.margemPercentual || 0).toFixed(1)}%</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

export default function RelatorioCustosServico({ data, type, filtro, setFiltro, onLimpar }) {
  const [openFiltro, setOpenFiltro] = useState(null);

  const onCloseFiltro   = () => setOpenFiltro(null);
  const limparFiltro    = () => { setOpenFiltro(false); onLimpar(); };
  const onConfirmFiltro = (f) => { setOpenFiltro(false); setFiltro(f); };

  if (!data) return null;

  const r = data.resumo;

  // ── Dados para gráfico — Venda vs Custo por Etapa (barras agrupadas) ────
  const dadosVendaCusto = (data.tabela || [])
    .filter((e) => e.quantidade > 0)
    .map((e) => ({ name: e.etapa, venda: e.valorVenda, custo: e.custo }));

  const exportarExcel = () => {
    const wb = XLSX.utils.book_new();
    const sheet = [];
    sheet.push(['Relatório de Custos por Serviço']);
    sheet.push([]);
    sheet.push(['1. Custos por Etapa']);
    sheet.push(['Etapa', 'Quantidade', 'Venda (R$)', 'Custo (R$)', 'Margem (R$)']);
    data.tabela.forEach((e) => sheet.push([e.etapa, e.quantidade, e.valorVenda, e.custo, e.margem]));
    sheet.push([]);
    sheet.push(['2. Por Tipo de Serviço']);
    sheet.push(['Tipo de Serviço', 'Quantidade', 'Venda (R$)', 'Custo (R$)', 'Margem (R$)', 'Margem (%)']);
    (data.porTipoServico || []).forEach((g) => sheet.push([g.grupo, g.quantidade, g.valorVenda, g.custo, g.margem, g.margemPercentual]));
    sheet.push([]);
    sheet.push(['3. Por Engenheiro Responsável']);
    sheet.push(['Responsável', 'Quantidade', 'Venda (R$)', 'Custo (R$)', 'Margem (R$)', 'Margem (%)']);
    (data.porResponsavel || []).forEach((g) => sheet.push([g.grupo, g.quantidade, g.valorVenda, g.custo, g.margem, g.margemPercentual]));
    sheet.push([]);
    sheet.push(['4. Resumo']);
    sheet.push(['Total Venda (R$)', r.totalVenda]);
    sheet.push(['Total Custo (R$)', r.totalCusto]);
    sheet.push(['Margem Total (R$)', r.margemTotal]);
    sheet.push(['Margem (%)', r.margemPercentual]);
    const ws = XLSX.utils.aoa_to_sheet(sheet);
    XLSX.utils.book_append_sheet(wb, ws, 'Custos Serviço');
    XLSX.writeFile(wb, 'relatorio-custos-servico.xlsx');
  };

  return (
    <Box mt={4}>
      {/* Barra de ações */}
      <Stack direction="row" justifyContent="flex-end" gap={2} mb={2}>
        <Button variant="outlined" onClick={() => setOpenFiltro(true)}>Filtro</Button>
        <Button variant="outlined" color="secondary" onClick={() => generatePdfRelatorioCustosServico(data, filtro)}>Exportar em PDF</Button>
        <Button variant="contained" sx={{ backgroundColor: LARANJA, '&:hover': { backgroundColor: LARANJA2 } }} onClick={exportarExcel}>
          Download Excel
        </Button>
      </Stack>

      {/* Filtros ativos */}
      <Stack direction="row" gap={1} mb={2} flexWrap="wrap">
        {filtro?.servico && <Chip label={`Serviço: ${filtro.servico}`} size="small" sx={{ backgroundColor: '#FFF3E0', color: LARANJA }} />}
        {filtro?.responsavel && <Chip label={`Engenheiro: ${filtro.responsavel.nome}`} size="small" sx={{ backgroundColor: '#FFF3E0', color: LARANJA }} />}
      </Stack>

      {/* ── KPIs ─────────────────────────────────────────────────────── */}
      <SecTitle>Resumo de Custos por Serviço</SecTitle>
      <Stack direction="row" gap={1.5} flexWrap="wrap" mb={3}>
        <KpiCard label="Total Venda (R$)"  valor={fmtBRL(r.totalVenda)} destaque />
        <KpiCard label="Total Custo (R$)"  valor={fmtBRL(r.totalCusto)} />
        <KpiCard label="Margem Total (R$)" valor={fmtBRL(r.margemTotal)} cor={r.margemTotal >= 0 ? VERDE : VERMELHO} />
        <KpiCard label="Margem (%)"        valor={`${Number(r.margemPercentual||0).toFixed(1)}%`} cor={r.margemPercentual >= 0 ? VERDE : VERMELHO} />
        <KpiCard label="Custo Médio (R$)"  valor={fmtBRL(r.custoMedio)} />
      </Stack>

      {/* ── Gráfico — Venda vs Custo por Etapa ──────────────────────────── */}
      <SecTitle>Venda vs Custo por Etapa</SecTitle>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={dadosVendaCusto} margin={{ left: 10, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
          <Tooltip formatter={(v) => fmtBRL(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="venda" name="Venda"  fill={LARANJA} radius={[4,4,0,0]} />
          <Bar dataKey="custo" name="Custo"  fill={CINZA2}  radius={[4,4,0,0]} />
        </BarChart>
      </ResponsiveContainer>

      <Divider sx={{ my: 3 }} />

      {/* ── Tabela principal ──────────────────────────────────────────── */}
      <SecTitle>Custos por Etapa</SecTitle>
      <TabelaCustos dados={data.tabela} />

      <Divider sx={{ my: 3 }} />

      {/* ── Segmentações ──────────────────────────────────────────────── */}
      <SecTitle>Segmentações</SecTitle>

      {data.porTipoServico?.length > 0 && (
        <>
          <Typography variant="body2" fontWeight="bold" color={LARANJA} mt={2} mb={1}>Por Tipo de Serviço</Typography>
          <TabelaSegmentacaoCusto dados={data.porTipoServico} />
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {data.porResponsavel?.length > 0 && (
        <>
          <Typography variant="body2" fontWeight="bold" color={LARANJA} mt={2} mb={1}>Por Engenheiro Responsável</Typography>
          <TabelaSegmentacaoCusto dados={data.porResponsavel} />
        </>
      )}

      {openFiltro && (
        <PopoverFiltroRelatorio filtro={filtro} open={openFiltro}
          onClose={onCloseFiltro} onLimpar={limparFiltro}
          onConfirm={onConfirmFiltro} type={type} />
      )}
    </Box>
  );
}